import React from 'react'

export type LoadingAnimationType = 
  | 'threeArchedCircle' 
  | 'progressiveDots' 
  | 'staggeredDotsWave'
  | 'spinner'
  | 'pulse'

interface LoadingAnimationProps {
  type?: LoadingAnimationType
  size?: 'small' | 'medium' | 'large'
  color?: string
  className?: string
}

const LoadingAnimation: React.FC<LoadingAnimationProps> = ({
  type = 'spinner',
  size = 'medium',
  color = '#FF6B35',
  className = ''
}) => {
  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'w-4 h-4'
      case 'large':
        return 'w-12 h-12'
      default:
        return 'w-8 h-8'
    }
  }

  const renderThreeArchedCircle = () => (
    <div className={`${getSizeClasses()} ${className}`}>
      <svg
        viewBox="0 0 24 24"
        className="w-full h-full animate-three-arched-circle"
        style={{ color }}
      >
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
          strokeDasharray="15.71 15.71"
          strokeDashoffset="0"
          strokeLinecap="round"
        />
      </svg>
    </div>
  )

  const renderProgressiveDots = () => (
    <div className={`flex items-center gap-1 ${className}`}>
      {[0, 1, 2].map((index) => (
        <div
          key={index}
          className={`w-2 h-2 rounded-full animate-progressive-dots`}
          style={{
            backgroundColor: color,
            animationDelay: `${index * 0.2}s`
          }}
        />
      ))}
    </div>
  )

  const renderStaggeredDotsWave = () => (
    <div className={`flex items-center gap-1 ${className}`}>
      {[0, 1, 2, 3, 4].map((index) => (
        <div
          key={index}
          className={`w-2 h-2 rounded-full animate-staggered-dots-wave`}
          style={{
            backgroundColor: color,
            animationDelay: `${index * 0.1}s`
          }}
        />
      ))}
    </div>
  )

  const renderSpinner = () => (
    <div className={`${getSizeClasses()} ${className}`}>
      <div
        className="w-full h-full border-2 border-gray-200 border-t-transparent rounded-full animate-spin"
        style={{ borderTopColor: color }}
      />
    </div>
  )

  const renderPulse = () => (
    <div className={`${getSizeClasses()} ${className}`}>
      <div
        className="w-full h-full rounded-full animate-pulse-slow"
        style={{ backgroundColor: color }}
      />
    </div>
  )

  switch (type) {
    case 'threeArchedCircle':
      return renderThreeArchedCircle()
    case 'progressiveDots':
      return renderProgressiveDots()
    case 'staggeredDotsWave':
      return renderStaggeredDotsWave()
    case 'pulse':
      return renderPulse()
    default:
      return renderSpinner()
  }
}

export default LoadingAnimation
